import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1623788975845-7d3e0adbae7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzbmVha2VyJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg2ODM0NjYwfDA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Sneaker in motion"
          className="h-[120%] w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-obsidian/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-44 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-aqua"
          data-testid="hero-overline"
        >
          Sneaker care studio — Collection & delivery
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.92] tracking-tighter text-white">
          <MaskedLine delay={0.15} className="pb-1">
            <span className="text-6xl sm:text-7xl lg:text-8xl">Clean.</span>
          </MaskedLine>
          <MaskedLine delay={0.3} className="pb-1">
            <span className="text-6xl text-aqua sm:text-7xl lg:text-8xl">& Revive.</span>
          </MaskedLine>
          <MaskedLine delay={0.45} className="pb-2">
            <span className="text-outline text-6xl sm:text-7xl lg:text-8xl">Delivered.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-md text-base text-zinc-400 sm:text-lg"
          data-testid="hero-subcopy"
        >
          Premium trainer cleaning with free local pick-up and doorstep delivery.
          Your grails, back to box-fresh.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <Link
            to="/book"
            data-testid="hero-book-button"
            className="border border-aqua bg-aqua px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-aqua"
          >
            Book a clean
          </Link>
          <button
            data-testid="hero-process-button"
            onClick={() => window.__lenis?.scrollTo("#process", { offset: -72 })}
            className="group flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-aqua"
          >
            See the process
            <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 flex flex-wrap gap-x-12 gap-y-4 border-t border-white/10 pt-6"
          data-testid="hero-stats"
        >
          {[
            ["100+", "Pairs revived"],
            ["48h", "Standard turnaround"],
            ["Free", "Local collection"],
          ].map(([stat, label]) => (
            <div key={label}>
              <p className="font-display text-2xl font-bold text-white">{stat}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
